import React, { useEffect, useState } from 'react'
import { fetchLoginCaptcha, login, session } from '../api'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  FormControlLabel,
  Checkbox,
  Link,
  Grid,
  Chip,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material'
import DashboardIcon from '@mui/icons-material/Dashboard'
import InventoryIcon from '@mui/icons-material/Inventory'
import StoreIcon from '@mui/icons-material/Store'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import ReceiptIcon from '@mui/icons-material/Receipt'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import { useUser } from '../contexts/UserContext'

export default function Login(){
  const role = 'admin'
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [remember, setRemember] = useState(true)
  const [forgotOpen, setForgotOpen] = useState(false)
  const [requestEmail, setRequestEmail] = useState('')
  const [captcha, setCaptcha] = useState({ challengeId: '', image: '' })
  const [captchaInput, setCaptchaInput] = useState('')
  const nav = useNavigate()
  const { setUser } = useUser()

  const refreshCaptcha = async () => {
    try {
      const nextCaptcha = await fetchLoginCaptcha()
      setCaptcha(nextCaptcha)
      setCaptchaInput('')
    } catch {
      setError('Unable to load verification. Please refresh the page.')
    }
  }

  useEffect(() => {
    refreshCaptcha()
  }, [])

  const submit = async e => {
    e.preventDefault()
    if (!captcha.challengeId || !captchaInput.trim()) {
      setError('Please complete the calculation verification.')
      return
    }
    setLoading(true)
    setError(null)
    try{
      await login(role, identifier, password, captcha.challengeId, captchaInput)
      // refresh session and set user in context so app pages are accessible immediately
      try{
        const s = await session()
        if(s?.authenticated) setUser(s.user)
      }catch(err){ /* ignore - navigation will still occur */ }
      nav('/');
    }catch(err){
      setError(err?.response?.data?.error || 'Login failed')
      await refreshCaptcha()
    } finally {
      setLoading(false)
    }
  }

  const sendResetEmail = () => {
    const to = 'support@owntrip.co.in'
    const subject = 'OwnTripPOS password reset request'
    const identifierText = requestEmail || identifier || 'my account'
    const body = `Hi OwnTrip Support,\n\nPlease help reset the OwnTripPOS POS & Billing password for this account:\n${identifierText}\n\nPlease let me know if you need any additional verification.\n\nThanks,\nOwnTripPOS user`
    const mailto = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    window.location.href = mailto
    setForgotOpen(false)
  }

  const floatingIcons = [
    { Icon: StoreIcon, top: '10%', left: '5%', delay: 0, duration: 20 },
    { Icon: PointOfSaleIcon, top: '20%', right: '8%', delay: 2, duration: 25 },
    { Icon: ReceiptIcon, top: '60%', left: '10%', delay: 4, duration: 22 },
    { Icon: InventoryIcon, top: '70%', right: '15%', delay: 1, duration: 24 },
    { Icon: LocalShippingIcon, top: '40%', left: '3%', delay: 3, duration: 26 },
    { Icon: CurrencyRupeeIcon, top: '80%', right: '5%', delay: 5, duration: 21 },
    { Icon: ShoppingCartIcon, top: '30%', right: '20%', delay: 2.5, duration: 23 },
    { Icon: AccountBalanceWalletIcon, top: '50%', left: '8%', delay: 1.5, duration: 27 },
    { Icon: DashboardIcon, top: '15%', left: '85%', delay: 4.5, duration: 19 },
    { Icon: ReceiptIcon, top: '85%', left: '75%', delay: 3.5, duration: 28 }
  ]

  return (
    <Box
      className="login-page"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at 20% 20%, rgba(25,118,210,0.08), transparent 35%), radial-gradient(circle at 80% 0%, rgba(123,31,162,0.08), transparent 30%), linear-gradient(135deg,#f7f9fc 0%,#e9edf5 100%)',
        backgroundImage: "linear-gradient(90deg, rgba(7,24,40,.15), rgba(7,24,40,.58)), url('/login-background.jpg')",
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        p: { xs: 2, md: 4 },
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Floating Background Icons */}
      {floatingIcons.map((item, idx) => (
        <Box
          key={idx}
          sx={{
            position: 'absolute',
            top: item.top,
            left: item.left,
            right: item.right,
            opacity: 0.06,
            pointerEvents: 'none',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
              '50%': { transform: 'translateY(-20px) rotate(5deg)' }
            },
            animation: `float ${item.duration}s ease-in-out ${item.delay}s infinite`
          }}
        >
          <item.Icon sx={{ fontSize: { xs: 40, md: 60 }, color: '#1976d2' }} />
        </Box>
      ))}
      <Container className="login-shell" component="main" maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={12}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 24px 80px rgba(0,0,0,0.12)',
            backdropFilter: 'blur(10px)',
            background: 'rgba(255,255,255,0.95)',
            maxWidth: 1100,
            width: '100%',
            mx: 'auto',
            border: '1px solid rgba(255,255,255,0.8)'
          }}
        >
          <Grid container className="login-grid" sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'nowrap' }}>
            <Grid
              className="login-visual-panel"
              item
              xs={12}
              md={6}
              sx={{ 
                flex: { md: '0 0 50%' }, 
                maxWidth: { md: '50%' },
                background: 'linear-gradient(135deg,#1565c0 0%,#6a1b9a 100%)',
                backgroundImage: "linear-gradient(145deg, rgba(7,39,58,.78), rgba(15,118,110,.62)), url('/login background.png')",
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                color: '#fff',
                p: { xs: 3, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.1), transparent 60%)',
                  pointerEvents: 'none'
                }
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Box
                  component="img"
                  src="/owntrippos-logo.png"
                  alt="OwnTripPOS Logo"
                  sx={{ 
                    width: 70, 
                    height: 70, 
                    mb: 2, 
                    borderRadius: '50%',
                    filter: 'drop-shadow(0 0 20px rgba(25,118,210,0.45))',
                    objectFit: 'cover'
                  }}
                />
                <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 1 }}>
                  Welcome back
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.95, lineHeight: 1.6 }}>
                  Smart billing, POS, inventory, and business operations in one connected workspace.
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mt: 3, flexWrap: 'wrap', gap: 1 }}>
                  {[
                    { icon: <DashboardIcon />, label: 'Smart Dashboards', borderColor: '#00e5ff' },
                    { icon: <InventoryIcon />, label: 'Inventory Control', borderColor: '#76ff03' },
                    { icon: <StoreIcon />, label: 'Unified Sales', borderColor: '#ff6e40' },
                    { icon: <ReceiptIcon />, label: 'Invoicing', borderColor: '#ffd740' },
                    { icon: <PointOfSaleIcon />, label: 'POS', borderColor: '#ea80fc' },
                    { icon: <InventoryIcon />, label: 'Stock Management', borderColor: '#00e676' },
                    { icon: <DashboardIcon />, label: 'Reports', borderColor: '#ff4081' }
                  ].map((item, idx) => (
                    <Chip
                      key={idx}
                      icon={item.icon}
                      label={item.label}
                      variant="filled"
                      size="medium"
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.18)', 
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        py: 2,
                        px: 1.2,
                        height: 'auto',
                        backdropFilter: 'blur(10px)',
                        border: `2px solid ${item.borderColor}`,
                        boxShadow: `0 0 12px ${item.borderColor}40`,
                        '& .MuiChip-icon': {
                          fontSize: '1.3rem',
                          color: '#fff'
                        },
                        '&:hover': { 
                          bgcolor: 'rgba(255,255,255,0.25)',
                          boxShadow: `0 0 20px ${item.borderColor}60`
                        }
                      }}
                    />
                  ))}
                </Stack>
              </Box>

              <Box sx={{ 
                mt: 3, 
                position: 'relative', 
                zIndex: 1,
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: 2,
                p: 2,
                backdropFilter: 'blur(10px)',
                bgcolor: 'rgba(255,255,255,0.08)',
                boxShadow: '0 0 15px rgba(255,255,255,0.15)'
              }}>
                <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 600, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                  Continue where you left off
                </Typography>
                <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.4)' }} />
                <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                  Quick access to POS terminal, invoices, and order history once you sign in.
                </Typography>
              </Box>
            </Grid>

            <Grid
              className="login-form-panel"
              item
              xs={12}
              md={6}
              sx={{
                flex: { md: '0 0 50%' }, 
                maxWidth: { md: '50%' },
                p: { xs: 3, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                backgroundColor: '#ffffff'
              }}
            >
              <Box className="login-form-heading" sx={{ 
                mb: 2.5,
                pb: 2,
                borderBottom: '3px solid transparent',
                borderImage: 'linear-gradient(90deg, #1565c0, #6a1b9a, #ea80fc) 1'
              }}>
                <Box className="login-card-logo-frame">
                  <Box component="img" src="/owntrippos-logo.png" alt="OwnTrip POS and Billing" className="login-card-logo" />
                </Box>
                <Typography component="h1" variant="h4" sx={{ fontWeight: 800, color: '#1a237e', mb: 1 }}>
                  Welcome Back
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Access your sales, inventory, invoices, and business reports.
                </Typography>
              </Box>

              <Box component="form" onSubmit={submit} sx={{ mt: 1 }}>
                {/* Role is fixed to admin by default */}

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Username or Email"
                  value={identifier}
                  onChange={e=>setIdentifier(e.target.value)}
                  autoFocus
                  sx={{ 
                    mb: 1.5,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#00e5ff',
                        borderWidth: '2px'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1565c0',
                        borderWidth: '2px',
                        boxShadow: '0 0 12px rgba(21,101,192,0.3)'
                      }
                    }
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={e=>setPassword(e.target.value)}
                  sx={{ 
                    mb: 1.5,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#ea80fc',
                        borderWidth: '2px'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#6a1b9a',
                        borderWidth: '2px',
                        boxShadow: '0 0 12px rgba(106,27,154,0.3)'
                      }
                    }
                  }}
                />

                <Box className="login-captcha">
                  <Box>
                    <Typography className="login-captcha-label">Verification</Typography>
                    <Typography variant="caption" color="text.secondary">Enter the answer shown in the image.</Typography>
                  </Box>
                  <Box className="login-captcha-row">
                    {captcha.image ? <Box component="img" src={captcha.image} alt="Calculation verification" className="login-captcha-image" /> : <Typography variant="caption">Loading...</Typography>}
                    <IconButton
                      type="button"
                      size="small"
                      aria-label="Generate a new calculation"
                      onClick={refreshCaptcha}
                    >
                      <RefreshRoundedIcon fontSize="small" />
                    </IconButton>
                    <TextField
                      size="small"
                      label="Answer"
                      value={captchaInput}
                      onChange={e => setCaptchaInput(e.target.value.replace(/\D/g, ''))}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      sx={{ width: 100 }}
                    />
                  </Box>
                </Box>

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  size="large"
                  sx={{
                    mt: 3,
                    mb: 1,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '1rem',
                    background: 'linear-gradient(135deg,#1565c0 0%,#6a1b9a 100%)',
                    boxShadow: '0 8px 24px rgba(25,118,210,0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      background: 'linear-gradient(135deg,#0d47a1 0%,#4a148c 100%)',
                      boxShadow: '0 12px 32px rgba(25,118,210,0.4)',
                      transform: 'translateY(-2px)'
                    },
                    '&:disabled': {
                      background: 'linear-gradient(135deg,#90caf9 0%,#ce93d8 100%)'
                    }
                  }}
                >
                  {loading ? 'Signing in...' : 'Continue to OwnTripPOS'}
                </Button>

                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <FormControlLabel
                    control={<Checkbox checked={remember} onChange={(e)=>setRemember(e.target.checked)} color="primary" />}
                    label={<Typography variant="body2">Remember me</Typography>}
                  />
                  <Button
                    type="button"
                    variant="text"
                    size="small"
                    onClick={() => {
                      setRequestEmail(identifier || '')
                      setForgotOpen(true)
                    }}
                    sx={{ ml: 1, fontWeight: 600, textTransform: 'none', px: 0, minWidth: 'auto' }}
                  >
                    Forgot password?
                  </Button>
                </Box>

                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Powered by{' '}
                    <Link
                      href="https://owntrip.co.in/"
                      target="_blank"
                      rel="noopener"
                      underline="none"
                      sx={{ color: 'inherit', fontWeight: 700 }}
                    >
                      OwnTrip
                    </Link>
                  </Typography>
                </Box>
              </Box>

              <Dialog className="login-reset-dialog" open={forgotOpen} onClose={() => setForgotOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Reset your OwnTripPOS password</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    We&apos;ll prepare a password-reset request for your OwnTripPOS billing workspace. Confirm your account email below.
                  </Typography>
                  <TextField
                    label="OwnTripPOS account email"
                    value={requestEmail}
                    onChange={e => setRequestEmail(e.target.value)}
                    fullWidth
                  />
                  <Typography variant="caption" color="text.secondary">
                    The request will be sent to OwnTrip support with the account details pre-filled.
                  </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button type="button" onClick={() => setForgotOpen(false)} variant="text">Cancel</Button>
                  <Button type="button" onClick={sendResetEmail} variant="contained">
                    Email OwnTrip support
                  </Button>
                </DialogActions>
              </Dialog>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  )
}
